import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatCHF } from "../../utils/formatCHF";
import { downloadExistingInvoice } from "../../utils/downloadExistingInvoice";
import {
  normalizeOrderTotalCents,
  getOrderStatusLabel,
  getOrderPaymentNotice,
  isPaidStatus,
  isUnpaidStatus,
} from "../../utils/orderPayment";

const OrderDetailsModal = ({ isOpen, order, onClose, helpers }) => {
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);

  const getClientPhone = helpers?.getClientPhone;
  const getInvoiceIdFromOrder = helpers?.getInvoiceIdFromOrder;
  const deliveryLabel = helpers?.deliveryLabel;
  const formatOrderDate = helpers?.formatOrderDate;
  const normalizeCents = helpers?.normalizeCents;

  const invoiceId = useMemo(() => {
    if (!order || !getInvoiceIdFromOrder) return 0;
    return getInvoiceIdFromOrder(order);
  }, [order, getInvoiceIdFromOrder]);

  const paid = isPaidStatus(order?.status);
  const unpaid = isUnpaidStatus(order?.status);

  const displayTotalCents = useMemo(() => {
    const helperNormalized = normalizeCents?.(
      order?.total_ttc,
      order?._moneyFactor
    );
    const directNormalized = normalizeOrderTotalCents(
      order?.total_ttc,
      order?._items || []
    );

    if (directNormalized > 0) return directNormalized;
    return helperNormalized ?? 0;
  }, [order, normalizeCents]);

  const close = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  const fetchInvoice = useCallback(async () => {
    if (!order) return;

    setInvoiceData(null);
    setInvoiceError("");

    if (!paid) {
      setInvoiceError(
        "Facture indisponible tant que le paiement n'est pas confirmé."
      );
      return;
    }

    if (!invoiceId) {
      setInvoiceError(
        "Facture indisponible (invoice_id manquant dans la commande)."
      );
      return;
    }

    setInvoiceLoading(true);
    try {
      const token = localStorage.getItem("user_token") || "";
      const url = `https://marchedeluxe.ch/services/get_invoice.php?invoice_id=${invoiceId}`;

      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => null);

      if (!data?.success) {
        setInvoiceError(data?.error || "Impossible de charger la facture.");
        return;
      }

      setInvoiceData(data);
    } catch (e) {
      setInvoiceError(
        e?.message || "Erreur réseau lors du chargement facture."
      );
    } finally {
      setInvoiceLoading(false);
    }
  }, [order, invoiceId, paid]);

  useEffect(() => {
    if (!isOpen) return;
    fetchInvoice();
  }, [isOpen, fetchInvoice]);

  const printInvoice = useCallback(() => {
    window.print();
  }, []);

  const downloadPdf = useCallback(async () => {
    if (!paid) {
      setInvoiceError(
        "Facture indisponible tant que le paiement n'est pas confirmé."
      );
      return;
    }

    if (!invoiceId) {
      setInvoiceError(
        "Facture indisponible (invoice_id manquant dans la commande)."
      );
      return;
    }

    try {
      setInvoiceError("");
      await downloadExistingInvoice({ invoiceId });
    } catch (e) {
      setInvoiceError(e?.message || "Impossible de télécharger la facture.");
    }
  }, [invoiceId, paid]);

  if (!isOpen || !order) return null;

  return (
    <div
      className="mdx-modal-backdrop"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Détails de commande et facture"
    >
      <style>{`
        .mdx-modal-backdrop {
          position:fixed; inset:0;
          background: rgba(0,0,0,.40);
          display:flex; align-items:flex-start; justify-content:center;
          padding: 24px;
          z-index: 9999;
        }
        .mdx-modal {
          width:min(980px, 100%);
          margin-top: 24px;
          background: #fff;
          border-radius: 16px;
          border: 1px solid rgba(0,0,0,.10);
          box-shadow: 0 20px 60px rgba(0,0,0,.25);
          overflow:hidden;
        }
        .mdx-modal-h {
          display:flex; justify-content:space-between; align-items:center;
          gap: 12px;
          padding: 14px 16px;
          border-bottom: 1px solid rgba(0,0,0,.06);
          background: rgba(0,0,0,.02);
        }
        .mdx-modal-title { margin:0; font-size: 14px; letter-spacing:.4px; text-transform: uppercase; }
        .mdx-meta { opacity:.75; font-size:12px; margin-top:4px; }
        .mdx-btn { border:1px solid rgba(0,0,0,.12); background:rgba(255,255,255,.8); padding:8px 10px; border-radius:10px; font-size:12px; cursor:pointer; }
        .mdx-btn:disabled { opacity:.5; cursor:not-allowed; }
        .mdx-modal-body { padding: 16px; display:grid; gap: 14px; }
        .mdx-modal-grid { display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .mdx-panel { border:1px solid rgba(0,0,0,.08); border-radius: 12px; padding: 12px; }
        .mdx-k { font-size: 12px; opacity:.7; }
        .mdx-v { font-weight: 700; margin-top: 4px; }
        .mdx-items { margin-top:10px; display:grid; gap:6px; }
        .mdx-item { display:flex; justify-content:space-between; gap:10px; padding:8px 10px; border-radius:10px; background:rgba(0,0,0,.03); }
        .mdx-note { margin-top:10px; padding:10px 12px; border-radius:10px; background:rgba(0,0,0,.03); font-style:italic; opacity:.85; }
        .mdx-error { margin: 10px 0 0; padding:12px; border-radius:12px; border:1px solid rgba(255,0,0,.25); background:rgba(255,0,0,.06); }
        .mdx-info { margin: 10px 0 0; padding:12px; border-radius:12px; border:1px solid rgba(212,175,55,.35); background:rgba(255,248,225,.85); color:#8b4513; }
        .mdx-success { margin: 10px 0 0; padding:12px; border-radius:12px; border:1px solid rgba(39,174,96,.28); background:rgba(39,174,96,.08); color:#1f6b3a; }
        .mdx-invoice-table { width:100%; border-collapse: collapse; font-size: 13px; }
        .mdx-invoice-table th, .mdx-invoice-table td { padding: 8px 10px; border-bottom: 1px solid rgba(0,0,0,.06); }
        .mdx-invoice-table th { text-align:left; font-size: 12px; text-transform: uppercase; opacity:.7; }
        .mdx-right { text-align:right; }

        @media (max-width: 900px) {
          .mdx-modal-grid { grid-template-columns: 1fr; }
        }

        @media print {
          .mdx-modal-backdrop { position: static; background: none; padding: 0; }
          .mdx-modal { box-shadow:none; border:none; border-radius:0; }
          .mdx-modal-h button { display:none; }
        }
      `}</style>

      <div className="mdx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mdx-modal-h">
          <div>
            <h3 className="mdx-modal-title">
              Commande #{order?.id ?? "—"} — Détails & Facture
            </h3>
            <div className="mdx-meta">
              {deliveryLabel?.(order?.delivery_mode)} •{" "}
              {getOrderStatusLabel(order?.status)} •{" "}
              {formatOrderDate?.(order?.created_at)}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="mdx-btn"
              onClick={downloadPdf}
              title="Télécharger la facture PDF"
              disabled={!invoiceId || !paid}
            >
              ⬇️ Télécharger PDF
            </button>

            <button
              type="button"
              className="mdx-btn"
              onClick={printInvoice}
              title="Imprimer"
            >
              🖨️ Imprimer
            </button>

            <button
              type="button"
              className="mdx-btn"
              onClick={close}
              title="Fermer (ESC)"
            >
              ✕ Fermer
            </button>
          </div>
        </div>

        <div className="mdx-modal-body">
          {unpaid && (
            <div className="mdx-info">
              <strong>Paiement non confirmé :</strong>{" "}
              {getOrderPaymentNotice(order?.status)}
            </div>
          )}

          {paid && (
            <div className="mdx-success">
              <strong>Paiement confirmé :</strong>{" "}
              {getOrderPaymentNotice(order?.status)}
            </div>
          )}

          <div className="mdx-modal-grid">
            <div className="mdx-panel">
              <div className="mdx-k">Client</div>
              <div className="mdx-v">{order?._client || "—"}</div>
              <div className="mdx-meta">
                Email : <strong>{order?.client_email || "—"}</strong>
              </div>
              <div className="mdx-meta">
                Tél : <strong>{getClientPhone?.(order) || "—"}</strong>
              </div>
            </div>

            <div className="mdx-panel">
              <div className="mdx-k">Total</div>
              <div className="mdx-v">{formatCHF(displayTotalCents)}</div>
              <div className="mdx-meta">
                Créneau :{" "}
                <strong>
                  {order?.horaire_pickpay?.trim() ? order.horaire_pickpay : "—"}
                </strong>
              </div>
              <div className="mdx-meta">
                Articles : <strong>{order?._items?.length ?? 0}</strong>
              </div>
            </div>
          </div>

          <div className="mdx-panel">
            <div className="mdx-k">Articles (commande)</div>
            <div style={{ marginTop: 8 }}>
              {order?._items?.length ? (
                <div className="mdx-items">
                  {order._items.map((it, idx) => {
                    const qty = Number(it?.qty ?? it?.quantite ?? 1) || 1;
                    const name =
                      it?.product_name ||
                      it?.name ||
                      it?.nom ||
                      (it?.id ? `Produit #${it.id}` : "Produit");
                    const p = it?.appliedPrice ?? it?.price ?? null;

                    return (
                      <div key={`${order.id}-${idx}`} className="mdx-item">
                        <div>
                          <strong>{qty}×</strong> {name}
                        </div>
                        <div style={{ whiteSpace: "nowrap" }}>
                          {p == null
                            ? "—"
                            : formatCHF(
                                normalizeCents?.(p, order._moneyFactor) ?? p
                              )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mdx-meta">Articles indisponibles.</div>
              )}
            </div>

            {order?.notes?.trim() ? (
              <div className="mdx-note" style={{ marginTop: 10 }}>
                Note : {order.notes}
              </div>
            ) : null}
          </div>

          <div className="mdx-panel">
            <div className="mdx-k">Facture</div>

            {invoiceLoading ? (
              <div className="mdx-meta" style={{ marginTop: 8 }}>
                Chargement facture…
              </div>
            ) : invoiceError ? (
              <div className="mdx-error" style={{ marginTop: 10 }}>
                <strong>Facture :</strong> {invoiceError}
              </div>
            ) : invoiceData?.invoice ? (
              <>
                <div className="mdx-meta" style={{ marginTop: 8 }}>
                  Invoice ID : <strong>{invoiceData.invoice?.id ?? "—"}</strong>
                  {" • "}
                  Statut : <strong>{invoiceData.invoice?.status ?? "—"}</strong>
                  {invoiceData.invoice?.invoice_number ? (
                    <>
                      {" • "}
                      N° : <strong>{invoiceData.invoice.invoice_number}</strong>
                    </>
                  ) : null}
                </div>

                <div style={{ marginTop: 10, overflowX: "auto" }}>
                  <table className="mdx-invoice-table">
                    <thead>
                      <tr>
                        <th>Ligne</th>
                        <th className="mdx-right">Qté</th>
                        <th className="mdx-right">Prix</th>
                        <th className="mdx-right">Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(invoiceData.lines || []).map((ln, i) => {
                        const qty = Number(ln?.qty ?? 1) || 1;
                        const unitCents =
                          Number(ln?.unit_price_cents ?? 0) || 0;
                        const totalCents =
                          Number(ln?.total_cents ?? unitCents * qty) || 0;

                        return (
                          <tr key={ln?.id ?? i}>
                            <td>
                              {ln?.name ||
                                ln?.label ||
                                ln?.product_name ||
                                `Ligne #${ln?.id ?? i + 1}`}
                            </td>
                            <td className="mdx-right">{qty}</td>
                            <td className="mdx-right">
                              {formatCHF(unitCents)}
                            </td>
                            <td className="mdx-right">
                              {formatCHF(totalCents)}
                            </td>
                          </tr>
                        );
                      })}

                      {(invoiceData.fees || []).length ? (
                        <>
                          <tr>
                            <td
                              colSpan={4}
                              style={{ paddingTop: 14, opacity: 0.7 }}
                            >
                              Frais
                            </td>
                          </tr>
                          {(invoiceData.fees || []).map((f, i) => {
                            const amountCents =
                              Number(
                                f?.amount_cents ??
                                  f?.amount ??
                                  f?.price ??
                                  f?.value ??
                                  0
                              ) || 0;

                            return (
                              <tr key={f?.id ?? `fee-${i}`}>
                                <td>
                                  {f?.label || f?.name || f?.code || "Frais"}
                                </td>
                                <td className="mdx-right">—</td>
                                <td className="mdx-right">—</td>
                                <td className="mdx-right">
                                  {formatCHF(amountCents)}
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="mdx-meta" style={{ marginTop: 8 }}>
                Aucune donnée facture.
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              className="mdx-btn"
              onClick={fetchInvoice}
              title="Recharger la facture"
            >
              🔄 Recharger facture
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
